
import React, { useState, useEffect, useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

interface ShirtDesigner2DProps {
  color: string;
  designUrl: string | null;
  showBackDesign: boolean;
  designSize: number;
  designPosition: { x: number, y: number };
  designRotation: number;
  frontImageUrl: string;
  backImageUrl: string;
  onPositionChange?: (position: { x: number, y: number }) => void;
  isAdjusting?: boolean;
}

const ShirtDesigner2D: React.FC<ShirtDesigner2DProps> = ({
  color,
  designUrl,
  showBackDesign,
  designSize,
  designPosition,
  designRotation,
  frontImageUrl,
  backImageUrl,
  onPositionChange,
  isAdjusting = false
}) => {
  const isMobile = useIsMobile();
  const [designElement, setDesignElement] = useState<HTMLImageElement | null>(null);
  const [designDimensions, setDesignDimensions] = useState({ width: 0, height: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (designUrl) {
      const img = new Image();
      img.onload = () => {
        setDesignElement(img);
        setDesignDimensions({
          width: img.width,
          height: img.height
        });
      };
      img.src = designUrl;
    } else {
      setDesignElement(null);
    }
  }, [designUrl]);
  
  // Handle drag operations for the design
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isAdjusting || !onPositionChange) return;
    
    e.preventDefault();
    setIsDragging(true);
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startPosX = designPosition.x;
    const startPosY = designPosition.y;
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!containerRef.current) return;
      
      const containerRect = containerRef.current.getBoundingClientRect();
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      
      // Calculate new position as percentage of container width/height
      const newX = startPosX + (deltaX / containerRect.width) * 100;
      const newY = startPosY + (deltaY / containerRect.height) * 100;
      
      // Restrict position to stay within reasonable bounds (20-80%)
      const boundedX = Math.max(10, Math.min(90, newX));
      const boundedY = Math.max(10, Math.min(90, newY));
      
      onPositionChange({ x: boundedX, y: boundedY });
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  // Calculate design position and size
  const getDesignStyles = () => {
    // Calculate aspect ratio-aware sizing
    let maxWidth = '60%';
    
    if (designElement) {
      const aspectRatio = designElement.width / designElement.height;
      if (aspectRatio > 1.5) {
        // Wide designs
        maxWidth = '70%';
      } else if (aspectRatio < 0.7) {
        // Tall designs
        maxWidth = '45%';
      }
    }
    
    return {
      width: `${Math.max(20, Math.min(90, designSize * 100))}%`,
      maxWidth: maxWidth,
      transform: `translate(-50%, -50%) rotate(${designRotation}deg)`,
      left: `${designPosition.x}%`,
      top: `${designPosition.y}%`,
      // Make the design border more visible when adjusting
      border: isAdjusting ? '2px dashed rgba(59, 130, 246, 0.8)' : 'none',
      boxShadow: isAdjusting ? '0 0 0 2px rgba(255, 255, 255, 0.5)' : 'none',
      padding: isAdjusting ? '8px' : '0px',
      cursor: isAdjusting ? 'move' : 'default',
      zIndex: 10, // Ensure design stays on top for interaction
      pointerEvents: isAdjusting ? 'auto' : 'none' as 'auto' | 'none', // Fixed typed pointer-events
    };
  };
  
  // Get color overlay style for t-shirt
  const getShirtColorStyle = () => {
    if (color.toLowerCase() === 'white' || color.toLowerCase() === '#ffffff') return {};
    
    return {
      backgroundColor: color.toLowerCase(),
      mixBlendMode: 'multiply' as const,
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.85,
      pointerEvents: 'none' as const
    };
  };
  
  // Get blend styles for the design to make it look printed on the shirt
  const getDesignBlendStyles = () => {
    return {
      mixBlendMode: 'multiply' as const,
      opacity: 0.95,
      filter: 'contrast(1.05) brightness(0.95)',
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain' as const,
    };
  };
  
  return (
    <div className="relative w-full h-full flex items-center justify-center" ref={containerRef}>
      {/* Base shirt image with transparent background */}
      <div className="relative w-full h-full flex items-center justify-center">
        <div className="relative w-4/5 max-w-md">
          <img 
            src={showBackDesign ? backImageUrl : frontImageUrl}
            alt={showBackDesign ? "T-shirt back" : "T-shirt front"}
            className="w-full h-auto object-contain"
          />
          
          {/* Color overlay - only apply to white parts of the shirt */}
          <div style={getShirtColorStyle()}></div>
        </div>
        
        {/* Design overlay */}
        {designUrl && (
          <div 
            className="absolute"
            style={getDesignStyles()}
            onMouseDown={handleMouseDown}
            onTouchStart={(e) => {
              if (isAdjusting && onPositionChange) {
                e.preventDefault();
                const touch = e.touches[0];
                setIsDragging(true);
                
                const startX = touch.clientX;
                const startY = touch.clientY;
                const startPosX = designPosition.x;
                const startPosY = designPosition.y;
                
                const handleTouchMove = (moveEvent: TouchEvent) => {
                  if (!containerRef.current) return;
                  const containerRect = containerRef.current.getBoundingClientRect();
                  const touchMove = moveEvent.touches[0];
                  
                  const deltaX = touchMove.clientX - startX;
                  const deltaY = touchMove.clientY - startY;
                  
                  // Calculate new position as percentage of container width/height
                  const newX = startPosX + (deltaX / containerRect.width) * 100;
                  const newY = startPosY + (deltaY / containerRect.height) * 100;
                  
                  // Use wider bounds for more flexibility
                  const boundedX = Math.max(10, Math.min(90, newX));
                  const boundedY = Math.max(10, Math.min(90, newY));
                  
                  onPositionChange({ x: boundedX, y: boundedY });
                };
                
                const handleTouchEnd = () => {
                  setIsDragging(false);
                  document.removeEventListener('touchmove', handleTouchMove);
                  document.removeEventListener('touchend', handleTouchEnd);
                };
                
                document.addEventListener('touchmove', handleTouchMove);
                document.addEventListener('touchend', handleTouchEnd);
              }
            }}
          >
            <img 
              src={designUrl} 
              alt="Design overlay"
              className="w-full h-auto object-contain"
              style={getDesignBlendStyles()}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ShirtDesigner2D;
